import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Send, Bot, User, Loader2, Wrench } from 'lucide-react';
import { apiClient } from '@sd/services/APIs';
import { SD_ENDPOINTS as EP } from '@sd/services/endpoints';
import { parseChatHistory } from '@sd/lib/parseChatHistory';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface ChatApiResponse {
  success?: { code: string; message: string };
  reply: string;
  reply_lines: string[];
  reply_html?: string;
  tools_used?: string[];
  error?: string | null;
}

interface ChatEntry {
  id: string;
  role: 'user' | 'bot';
  text?: string;
  reply_lines?: string[];
  tools_used?: string[];
  timestamp: Date;
  loading?: boolean;
  isError?: boolean;
}

const SUGGESTED_PROMPTS = [
  'Show in progress status tickets count',
  'How many open tickets are there?',
  'Show me tickets assigned to me',
  'List overdue tickets',
  'Show high priority tickets',
];

const WELCOME_ENTRY: ChatEntry = {
  id: 'welcome',
  role: 'bot',
  reply_lines: [
    'Hello! I am your Service Desk AI assistant.',
    'You can ask me about tickets, agents, SLAs, and more.',
  ],
  timestamp: new Date(),
};

export function AdminChatMeetings() {
  const [messages, setMessages] = useState<ChatEntry[]>([WELCOME_ENTRY]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    let cancelled = false;

    const loadHistory = async () => {
      try {
        const { data } = await apiClient.get(EP.chat.history);
        if (cancelled) return;
        const history = parseChatHistory(data);
        if (!history.length) return;
        setMessages(prev => {
          const alreadyChatting = prev.some(
            m => m.role === 'user' && !String(m.id).startsWith('hist-')
          );
          if (alreadyChatting) return prev;
          return [
            WELCOME_ENTRY,
            ...history.map(entry => ({
              ...entry,
              id: String(entry.id).startsWith('hist-') ? entry.id : `hist-${entry.id}`,
            })),
          ];
        });
      } catch {
        // Keep the existing welcome thread if history is unavailable.
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    };

    void loadHistory();
    return () => {
      cancelled = true;
    };
  }, []);

  const sendMessage = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;

    const userEntry: ChatEntry = {
      id: `user-${Date.now()}`,
      role: 'user',
      text: trimmed,
      timestamp: new Date(),
    };

    const botLoadingEntry: ChatEntry = {
      id: `bot-${Date.now()}`,
      role: 'bot',
      loading: true,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userEntry, botLoadingEntry]);
    setInput('');
    setIsLoading(true);

    try {
      const { data } = await apiClient.post<ChatApiResponse>(EP.chat.message, {
        message: trimmed,
      });

      setMessages(prev =>
        prev.map(m =>
          m.id === botLoadingEntry.id
            ? {
                ...m,
                loading: false,
                reply_lines: data.reply_lines?.length
                  ? data.reply_lines
                  : [data.reply ?? 'No response received.'],
                tools_used: data.tools_used,
              }
            : m
        )
      );
    } catch {
      toast.error('Failed to get response from AI assistant');
      setMessages(prev =>
        prev.map(m =>
          m.id === botLoadingEntry.id
            ? {
                ...m,
                loading: false,
                reply_lines: ['Something went wrong. Please try again.'],
                isError: true,
              }
            : m
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex shrink-0 items-center gap-3 border-b bg-card px-6 py-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10">
          <Bot className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-foreground">AI Chat Assistant</h1>
          <p className="text-xs text-muted-foreground">Ask anything about your Service Desk</p>
        </div>
        <Badge variant="outline" className="ml-auto border-green-300 bg-green-50 text-green-700 text-xs">
          Online
        </Badge>
      </div>

      {/* Messages Area */}
      <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
        {historyLoading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading previous chats...
          </div>
        )}
        {messages.map(msg => (
          <div
            key={msg.id}
            className={cn(
              'flex items-start gap-3',
              msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            {/* Avatar */}
            <div
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                msg.role === 'user'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              )}
            >
              {msg.role === 'user' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
            </div>

            {/* Bubble */}
            <div className={cn('max-w-[75%] space-y-1', msg.role === 'user' && 'items-end flex flex-col')}>
              {/* User message */}
              {msg.role === 'user' && msg.text && (
                <div className="rounded-2xl rounded-tr-sm bg-primary px-4 py-2.5 text-sm text-primary-foreground">
                  {msg.text}
                </div>
              )}

              {/* Bot — loading */}
              {msg.role === 'bot' && msg.loading && (
                <div className="flex items-center gap-2 rounded-2xl rounded-tl-sm bg-muted px-4 py-3 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Thinking...</span>
                </div>
              )}

              {/* Bot — reply_lines */}
              {msg.role === 'bot' && !msg.loading && msg.reply_lines && (
                <div
                  className={cn(
                    'rounded-2xl rounded-tl-sm px-4 py-3 text-sm',
                    msg.isError
                      ? 'bg-red-50 text-red-700'
                      : 'bg-muted text-foreground'
                  )}
                >
                  {msg.reply_lines.map((line, i) => (
                    <p key={i} className={cn('leading-relaxed', i > 0 && 'mt-1')}>
                      {line}
                    </p>
                  ))}
                </div>
              )}

              {/* Tools used badge */}
              {msg.tools_used && msg.tools_used.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {msg.tools_used.map(tool => (
                    <span
                      key={tool}
                      className="inline-flex items-center gap-1 rounded-full bg-primary/8 px-2 py-0.5 text-[10px] font-medium text-primary"
                    >
                      <Wrench className="h-2.5 w-2.5" />
                      {tool.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              )}

              {/* Timestamp */}
              <p className="px-1 text-[10px] text-muted-foreground">
                {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Suggested prompts — show only when no real conversation yet */}
      {!historyLoading && messages.length <= 1 && (
        <div className="shrink-0 border-t bg-muted/30 px-6 py-3">
          <p className="mb-2 text-xs font-medium text-muted-foreground">Suggested questions:</p>
          <div className="flex flex-wrap gap-2">
            {SUGGESTED_PROMPTS.map(prompt => (
              <button
                key={prompt}
                type="button"
                onClick={() => sendMessage(prompt)}
                className="rounded-full border bg-card px-3 py-1.5 text-xs text-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="shrink-0 border-t bg-card px-6 py-4">
        <div className="flex items-center gap-3">
          <Input
            placeholder="Ask about tickets, agents, SLA..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            className="flex-1 rounded-full px-4"
          />
          <Button
            size="icon"
            onClick={() => sendMessage(input)}
            disabled={isLoading || !input.trim()}
            className="h-10 w-10 shrink-0 rounded-full"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
        <p className="mt-2 text-center text-[10px] text-muted-foreground">
          Press Enter to send · AI responses are generated from live ticket data
        </p>
      </div>
    </div>
  );
}
